import * as React from 'react'

/**
 * A stable, unique-per-mount id, without `React.useId`.
 *
 * `@types/react` is pinned to 17 in this repo (AGENTS.md), so `React.useId`
 * has no type even though the React 18 runtime provides it. A ref-held
 * counter gives the same guarantee — stable across re-renders, unique per
 * mount — with no dependency on those types.
 *
 * Used by the `Checkbox` and `Radio` seams' shadcn halves to pair a generated
 * `<label htmlFor>` with the control it names when the caller passes no `id`.
 * That pairing is not cosmetic: the control those seams render is a
 * `<button>`, so a label cannot own it by nesting the way it owns a native
 * `<input>` — and `jsx-a11y/label-has-associated-control` cannot see through
 * the primitive to know that either.
 *
 * Ported from the `update-design` reference branch verbatim.
 */
let counter = 0

export function useStableId(prefix: string): string {
  const ref = React.useRef<string | null>(null)
  if (ref.current === null) {
    counter += 1
    ref.current = `${prefix}-${counter}`
  }
  return ref.current
}
